/*
 * cf-checkout-smooth.js — Click Fencing — 2026-09-07
 *
 * Pete: "i think there is too much noise between finishing the calculator and going to
 * checkout. make that part smoother so we get more checkouts."
 *
 * WHAT WAS ACTUALLY IN THE WAY (measured on /pages/quote-review, phone 375x812)
 * ----------------------------------------------------------------------------
 *   - the page is 5.3 phone screens tall and the only pay button sits 2.9 screens down,
 *     below Section Details, Summary, Full Breakdown and a disclaimer;
 *   - clicking it opens a modal demanding SIX required fields — name, email, phone,
 *     address, suburb, postcode;
 *   - Shopify checkout then asks for name, email and address all over again.
 *
 * A note on a claim that did NOT survive checking: the page looks like it has two pay
 * buttons, because #CartDrawer-Checkout is in the DOM with a real bounding box. It is
 * inside the CLOSED cart drawer. `offsetParent !== null` does NOT detect
 * `visibility:hidden` on an ancestor — this file's reallyVisible() does. There is one
 * visible CTA and nothing here should hide the drawer's button.
 *
 * WHAT THIS DOES
 * --------------
 * 1. A sticky bar with the total and the pay button, shown only while the real CTA is off
 *    screen, so the way to pay is always one tap away. It CLICKS THE REAL BUTTON rather
 *    than reimplementing it.
 * 2. Drops the four duplicated fields to optional — phone, address, suburb, postcode —
 *    leaving name and email required. Two fields instead of six.
 * 3. Prefills every field it can from the signed-in customer.
 * 4. Collapses Section Details and Full Breakdown behind a "show" toggle. The customer
 *    approved this exact quote on the calculator's own Review step one click ago; the
 *    header still carries the reference and the total, and Summary stays open.
 *
 * WHY THE DETAILS STEP IS NOT REMOVED ALTOGETHER
 * ----------------------------------------------
 * Because it writes the order note, and the order note is the picking list. Every Click
 * Fencing order carries the quote name and reference, the calculator, the pricing type,
 * the GRAND TOTAL, every part with its SKU and variant id, and the manufacturing layout
 * line (`S1|Section 1|len=9000|panels=3000:panel,970:gate,...`). The supplier PO and the
 * warehouse pick from that. Deleting the step to save two taps would leave orders with
 * nothing to pick. So the step stays and the FRICTION comes off it instead.
 *
 * WHY DROPPING THE ADDRESS REQUIREMENT IS SAFE. The note this same page writes says it
 * outright: "The binding answer is the shipping line on this order - if they differ, they
 * changed it at checkout and the order wins." Shopify collects and validates a real
 * shipping address at checkout; a second hand-typed copy beforehand is not the source of
 * truth and never was. Name and email stay required because they identify the customer
 * and are what the CRM contact is built from.
 *
 * Everything is additive and reversible: the CTA is the site's own button, the fields are
 * only un-required, and the collapse is a class on a wrapper this file inserts.
 */
(function () {
  "use strict";

  if (!/\/pages\/quote-review/.test(location.pathname)) return;
  if (window.__cfCheckoutSmooth) return;
  window.__cfCheckoutSmooth = true;

  var TRIES = 0, MAX_TRIES = 200;
  var BAR_ID = "cf-cs-bar";

  // offsetParent !== null is NOT a visibility test - it misses visibility:hidden and
  // opacity:0 on an ancestor, and it is null for any position:fixed element. This is the
  // check that stopped a false "two checkout buttons" finding.
  function reallyVisible(el) {
    if (!el) return false;
    var r = el.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    var n = el;
    while (n && n.nodeType === 1) {
      var cs = window.getComputedStyle(n);
      if (cs.display === "none" || cs.visibility === "hidden" || cs.opacity === "0") return false;
      n = n.parentElement;
    }
    return true;
  }

  function cta() { return document.getElementById("qr-enquire-btn"); }

  function readTotal() {
    try {
      var h = document.querySelector(".qr-header__inner");
      if (!h) return null;
      var m = h.textContent.replace(/\s+/g, " ").match(/Estimated Total\s*(\$[\d,]+\.\d{2})/i);
      return m ? m[1] : null;
    } catch (e) { return null; }
  }

  /* ---------------------------------------------------------------- 2 + 3 ---- */

  // 🔴 DO NOT MAKE THESE FIELDS OPTIONAL. It was tried on 2026-09-07 and it DEAD-ENDS
  // THE CUSTOMER AT PAYMENT.
  //
  // Removing the HTML `required` attribute is not enough: quote-review.js runs its OWN
  // JavaScript check inside the submit handler and refuses with "Please fill in all
  // fields with a valid 4-digit postcode." So the labels read "(optional)", the customer
  // leaves them blank as invited, and the button then silently does nothing — no cart
  // call is made at all (verified by intercepting fetch: zero requests). That is the
  // exact fault Pete's brief forbids: "Never dead-end a customer. A block belongs at the
  // point of design, never at payment."
  //
  // To genuinely cut these four fields, the check inside quote-review.js has to go too,
  // and the address has to keep reaching the supplier PO for delivery orders. Until
  // someone does both, they stay required and honestly labelled.
  //
  // What still helps and is safe: prefill() below fills them for a signed-in customer,
  // and the sticky bar removes the scrolling. Those are the wins; this was not one.
  function relaxFields() {
    // Deliberately does nothing. Kept as the place the explanation lives.
  }

  // Only ever fills a field that is EMPTY, so it cannot overwrite something typed.
  function prefill() {
    var c = window.cfCustomer;
    if (!c || !c.loggedIn) return;
    var map = {
      "qr-f-name": c.name || ((c.firstName || "") + " " + (c.lastName || "")).trim(),
      "qr-f-email": c.email,
      "qr-f-phone": c.phone,
      "qr-f-address": c.address1 || c.address,
      "qr-f-suburb": c.city || c.suburb,
      "qr-f-postcode": c.zip || c.postcode
    };
    Object.keys(map).forEach(function (id) {
      var el = document.getElementById(id);
      if (el && !el.value && map[id]) {
        el.value = String(map[id]);
        try { el.dispatchEvent(new Event("input", { bubbles: true })); } catch (e) {}
      }
    });
  }

  /* ------------------------------------------------------------------- 4 ---- */

  function collapsible(sec, label, openByDefault) {
    if (!sec || sec.getAttribute("data-cf-collapsed")) return;
    var head = sec.querySelector("h1,h2,h3");
    if (!head) return;
    sec.setAttribute("data-cf-collapsed", "1");

    var body = document.createElement("div");
    body.className = "cf-cs-body";
    var kids = [], n;
    for (n = 0; n < sec.children.length; n++) {
      if (sec.children[n] !== head) kids.push(sec.children[n]);
    }
    for (n = 0; n < kids.length; n++) body.appendChild(kids[n]);
    sec.appendChild(body);

    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "cf-cs-toggle";
    btn.style.cssText =
      "margin-left:10px;padding:4px 12px;font-size:.82rem;font-weight:600;cursor:pointer;" +
      "border:1px solid #cbd5e1;background:#fff;color:#1F3A5F;border-radius:999px;vertical-align:middle;";
    head.appendChild(btn);

    function paint(open) {
      body.style.display = open ? "" : "none";
      btn.textContent = open ? "Hide" : label;
    }
    btn.addEventListener("click", function (e) {
      e.preventDefault();
      paint(body.style.display === "none");
    });
    paint(!!openByDefault);
  }

  function collapseRepeats() {
    var secs = document.querySelectorAll("#qr-content .qr-section");
    for (var i = 0; i < secs.length; i++) {
      var h = secs[i].querySelector("h1,h2,h3");
      var t = h ? h.textContent.replace(/\s+/g, " ").trim() : "";
      if (/^Section Details/i.test(t)) collapsible(secs[i], "Show the drawings", false);
      else if (/^Full Breakdown/i.test(t)) collapsible(secs[i], "Show every part", false);
    }
  }

  /* ------------------------------------------------------------------- 1 ---- */

  function buildBar() {
    if (document.getElementById(BAR_ID)) return document.getElementById(BAR_ID);
    var bar = document.createElement("div");
    bar.id = BAR_ID;
    bar.setAttribute("role", "region");
    bar.setAttribute("aria-label", "Checkout");
    bar.style.cssText =
      "position:fixed;left:0;right:0;bottom:0;z-index:998;display:none;" +
      "align-items:center;gap:12px;padding:10px 14px calc(10px + env(safe-area-inset-bottom));" +
      "background:rgba(255,255,255,.97);border-top:1px solid #e2e8f0;" +
      "box-shadow:0 -3px 14px rgba(16,24,40,.10);" +
      "font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;";

    var left = document.createElement("div");
    left.style.cssText = "flex:1 1 auto;min-width:0;line-height:1.25;";
    var lab = document.createElement("div");
    lab.style.cssText = "font-size:.72rem;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;";
    lab.textContent = "Total inc GST";
    var amt = document.createElement("div");
    amt.className = "cf-cs-amt";
    amt.style.cssText = "font-size:1.12rem;font-weight:700;color:#1F3A5F;white-space:nowrap;";
    left.appendChild(lab); left.appendChild(amt);

    var go = document.createElement("button");
    go.type = "button";
    go.className = "cf-cs-go";
    go.textContent = "Continue to checkout";
    go.style.cssText =
      "flex:0 0 auto;padding:13px 20px;font-size:1rem;font-weight:700;color:#fff;" +
      "background:#0e9ed5;border:0;border-radius:999px;cursor:pointer;max-width:60%;";
    go.addEventListener("click", function (e) {
      e.preventDefault();
      var b = cta();
      if (!b) return;
      b.click();                      // the site's own handler - never reimplemented
      try { b.scrollIntoView({ block: "center", behavior: "smooth" }); } catch (e2) {}
    });

    bar.appendChild(left);
    bar.appendChild(go);
    document.body.appendChild(bar);
    return bar;
  }

  function modalOpen() {
    return reallyVisible(document.getElementById("qr-modal-overlay"));
  }

  // 🔴 THE BAR'S VISIBILITY MUST NOT DEPEND ON ANYTHING THE BAR ITSELF CHANGES.
  //
  // The version before this one hid the bar while the real CTA was on screen, and
  // toggled document.body padding-bottom to make room for it. That is an oscillator:
  // showing the bar adds 78px, which lengthens the page, which fires scroll, which
  // re-runs this function, which can flip the CTA in or out of view, which removes the
  // padding, which shortens the page... round and round at full speed. Nothing is
  // logged and no error is thrown - the page simply stops responding, and a one-line
  // script in the console times out. It cost two deploys to find.
  //
  // So: the padding is set ONCE at install and never touched again, and visibility is
  // decided by window.scrollY, which the bar cannot influence. Every write is still
  // guarded by a comparison so a no-op pass writes nothing at all.
  function refreshBar() {
    var bar = document.getElementById(BAR_ID);
    var b = cta();
    if (!bar || !b) return;

    var t = readTotal();
    var amt = bar.querySelector(".cf-cs-amt");
    if (amt && amt.textContent !== (t || "")) amt.textContent = t || "";

    // Hidden while the modal is up - it would sit on top of the form.
    var show = !!t && !modalOpen() && reallyVisible(b) && (window.scrollY || 0) > 300;

    var want = show ? "flex" : "none";
    if (bar.style.display !== want) bar.style.display = want;
  }

  /* --------------------------------------------------------------- install --- */

  function install() {
    var b = cta();
    if (!b) return false;

    buildBar();
    relaxFields();
    prefill();
    collapseRepeats();

    // Set once, never toggled - see the note on refreshBar. Constant padding means the
    // page height does not change when the bar appears, so nothing can oscillate.
    document.body.style.paddingBottom = "78px";

    refreshBar();

    window.addEventListener("scroll", refreshBar, { passive: true });
    window.addEventListener("resize", refreshBar);

    // 🔴 NO MutationObserver HERE, DELIBERATELY. An earlier version watched
    // document.body {childList, subtree} and re-applied on every mutation. It wedged the
    // page so hard that a one-line script could not run. The cause is not this file's
    // own writes - those are guarded - it is that /pages/quote-review already loads
    // ELEVEN other quote scripts, several of which re-render the panel in response to
    // DOM changes. Two observers each reacting to the other's writes is a feedback loop
    // neither one can see in isolation.
    //
    // Polling is the right tool here: the work is trivial, once a second is far more
    // often than a human changes a quantity, and it cannot be re-entered by anything
    // else on the page. Do not "optimise" this back into an observer.
    setInterval(function () {
      try { relaxFields(); prefill(); refreshBar(); } catch (e) {}
    }, 1000);

    // The modal is built lazily, so catch it the moment the customer asks for it.
    b.addEventListener("click", function () {
      setTimeout(function () { try { relaxFields(); prefill(); refreshBar(); } catch (e) {} }, 60);
    });

    try { console.log("[cf-checkout-smooth] installed"); } catch (e) {}
    return true;
  }

  function tryInstall() {
    var done = false;
    try { done = install(); } catch (e) {
      try { console.warn("[cf-checkout-smooth] " + (e && e.message)); } catch (e2) {}
      done = true;                    // never loop on a thrown error
    }
    if (!done && ++TRIES < MAX_TRIES) setTimeout(tryInstall, 100);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", tryInstall);
  } else {
    tryInstall();
  }
})();
